import React, { useState, useMemo } from "react";
import { Input, Typography, Badge } from "antd";
import { SearchOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { SECTION_REGISTRY, SECTION_CATEGORIES, isSectionAllowedInGroup } from "./autoload";
import { CMS_THEME } from "./constant";

const { Text } = Typography;

export default function ComponentLibrary({
  onAdd,
  insertAt,
  onClearInsert,
  activeSections = [],
  pageGroup,
}) {
  const [search, setSearch] = useState("");

  const componentCounts = useMemo(() => {
    const counts = {};
    activeSections.forEach((sec) => {
      if (sec?.type) {
        counts[sec.type] = (counts[sec.type] || 0) + 1;
      }
    });
    return counts;
  }, [activeSections]);

  const categories = useMemo(() => {
    const cats = {};
    Object.values(SECTION_CATEGORIES).forEach((catName) => {
      const sections = SECTION_REGISTRY.filter(
        (s) => s.category === catName && isSectionAllowedInGroup(s, pageGroup),
      );
      if (sections.length > 0) cats[catName] = sections;
    });
    return cats;
  }, [pageGroup]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;

    const result = {};
    Object.keys(categories).forEach((catName) => {
      const filtered = categories[catName].filter(
        (sec) =>
          sec.name.toLowerCase().includes(q) ||
          (sec.description || "").toLowerCase().includes(q),
      );
      if (filtered.length > 0) {
        result[catName] = filtered;
      }
    });
    return result;
  }, [categories, search]);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      {insertAt !== null && (
        <div
          style={{
            background: "#fef2f2",
            borderBottom: "1px solid #fca5a5",
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text style={{ fontSize: 11, color: "#8b0000", fontWeight: 600 }}>
            ↓ Insert at pos #{insertAt + 1}
          </Text>
          <button
            type="button"
            onClick={onClearInsert}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            <CloseOutlined />
          </button>
        </div>
      )}

      <div
        style={{ padding: "10px 10px 8px", borderBottom: "1px solid #f0f0f0" }}
      >
        <Text
          strong
          style={{
            fontSize: 10,
            display: "block",
            marginBottom: 6,
            color: "#6b7280",
            letterSpacing: "0.05em",
          }}
        >
          LIBRARY
        </Text>
        <Input
          prefix={<SearchOutlined style={{ color: "#9ca3af", fontSize: 12 }} />}
          placeholder="Search sections…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ borderRadius: 4, fontSize: 12 }}
          allowClear
        />
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "6px 6px 16px",
        }}
      >
        {Object.keys(filteredCategories).map((catName) => (
          <div key={catName} style={{ marginBottom: 10 }}>
            <div
              style={{
                padding: "4px 6px 2px",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#9ca3af",
                  letterSpacing: "0.04em",
                }}
              >
                {catName}
              </Text>
            </div>

            {filteredCategories[catName].map((sec) => {
              const count = componentCounts[sec.type] || 0;
              const hasCount = count > 0;

              return (
                <div
                  key={sec.type}
                  onClick={() => onAdd(sec)}
                  style={{
                    padding: "5px 8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderRadius: 4,
                    margin: "1px 0",
                    border: hasCount
                      ? "1px solid #fca5a5"
                      : "1px solid transparent",
                    background: hasCount ? "#fef2f2" : "transparent",
                    transition: "all 0.12s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!hasCount) e.currentTarget.style.background = "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    if (!hasCount)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <Text
                    style={{
                      fontSize: 12,
                      color: hasCount ? "#8b0000" : "#374151",
                      fontWeight: hasCount ? 600 : 400,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {sec.name}
                  </Text>

                  {hasCount ? (
                    <Badge
                      count={count}
                      style={{
                        backgroundColor: "#8b0000",
                        fontSize: 10,
                        fontWeight: 700,
                        height: 16,
                        lineHeight: "16px",
                        minWidth: 16,
                        padding: "0 4px",
                      }}
                    />
                  ) : (
                    <PlusOutlined style={{ color: "#9ca3af", fontSize: 11 }} />
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {Object.keys(filteredCategories).length === 0 && (
          <div style={{ padding: 16, textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              No sections match "{search}"
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
